import {useScreenSize} from '@/common-pages/hooks/size.hooks';
import {cn, goTo} from '@/utils';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {ReactComponent as QuestionIcon} from '@/assets/icons/spin-actual/question.svg';
import {ReactComponent as CloseIcon} from '@/assets/icons/spin-actual/close.svg';
import {Button} from 'antd-mobile';
import {useSequentialModalContext} from '@/components/basic/sequential-modal/context';
import {useWheelContainer} from './wheel-container.hooks';
import {Trans, useTranslation} from 'react-i18next';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/css';
import 'swiper/css/autoplay';
import {Autoplay} from 'swiper/modules';
import SpinActualRules, {ModalRef} from './modal/rules';
import {useLogin} from '@/utils/state-hooks';
import PrizeModal, {
  generateMockData,
  PrizeData,
  PrizeRef,
  UserTopDataItem,
} from './modal/prize';
import {
  getActualSpinInfo,
  getSpinCountInfo,
  getSpinTop,
  getUserWeightInfo,
  TurntableLevel,
  userDraw,
} from './service';
import globalStore from '@/services/global.state';
import {DEFAULT_PRIZE_LIST} from './const';

// 转盘容器组件
interface WheelContainerProps {
  style?: React.CSSProperties;
}

const WheelContainer: React.FC<WheelContainerProps> = ({style}) => {
  return (
    <div
      className="size-full bg-full bg-no-repeat"
      style={{
        backgroundImage: `url(${require('@/assets/imgs/spin-actual/spin-pic.webp')})`,
        ...style,
      }}
    />
  );
};

const baseRotateCount = 4;
const SpinActualContent = () => {
  const login = useLogin();
  const {t} = useTranslation();
  const beforeSpin = () => {
    if (!login) {
      goTo('Login');
      onClose();
      return false;
    }
    if (spinCount <= 0) {
      // globalStore.globalWaringTotal(t('spinActual.tip.noSpinCount'));
      goTo('Recharge');
      onClose();
      return false;
    }
    return true;
  };
  const updateSpinCount = () => {
    getSpinCountInfo().then(res => {
      setSpinCount(res);
    });
  };

  const requestSpin = () => {
    return new Promise<{spinEndRotate: number; prize: PrizeData} | false>(
      resolve => {
        userDraw()
          .then(res => {
            if (!res) {
              resolve(false);
              globalStore.globalWaringTotal(t('spinActual.tip.spinError'));
              return;
            }
            const awardName = res.awardName;
            const awardItem = turnInfoList.find(
              item => item.name === awardName,
            );
            if (!awardItem) {
              resolve(false);
              globalStore.globalWaringTotal(t('spinActual.tip.spinError'));
              return;
            }
            const deg =
              (awardItem.startDeg || 0) +
              ((awardItem.endDeg || 0) - (awardItem.startDeg || 0)) *
                Math.random();
            resolve({
              spinEndRotate: deg,
              prize: {
                name: res.awardName,
                pic: res.awardUrl,
                isActual: awardItem.level != null && awardItem.level <= 4,
                customerImUrl: res.customerImUrl,
              },
            });
            updateSpinTop();
          })
          .catch(e => {
            console.log(e);
            if ((e as any).data.msg?.toLowerCase().includes('risk')) {
              // risk user, award intercepted
              globalStore.globalWaringTotal((e as any).data.msg);
              // 但是spinCount需要更新，因为它依然消耗了转盘次数
              updateSpinCount();
            }
            resolve(false);
          });
      },
    );
  };
  const prizeRef = useRef<PrizeRef>(null);
  const handleShowPrize = (prize: PrizeData) => {
    prizeRef.current?.show(prize);
    updateSpinCount();
  };
  const {handleSpin, rotation, isSpinning} = useWheelContainer({
    baseRotateCount,
    beforeSpin,
    requestSpin,
    onShowPrize: handleShowPrize,
  });
  const {goToNext, onClose} = useSequentialModalContext();

  const {screenHeight, screenWidth} = useScreenSize();

  const smallHeight = screenHeight / screenWidth < 750 / 375;

  const rulesRef = useRef<ModalRef>(null);

  const handleRules = () => {
    rulesRef.current?.open();
  };
  const [turnId, setTurnId] = useState<string | number | null>(null);
  const [turnInfoList, setTurnInfoList] = useState<TurntableLevel[]>([]);
  const prizeList = useMemo(() => {
    return turnInfoList?.length
      ? turnInfoList
          .filter(item => item.level != null && item.level < 9)
          .map(item => ({
            name: item.name || '',
            pic: '',
            isActual: item.level != null && item.level <= 4,
          }))
      : DEFAULT_PRIZE_LIST;
  }, [turnInfoList]);
  const [userTopList, setUserTopList] = useState<UserTopDataItem[]>([]);

  const updateSpinTop = () => {
    const actualCount = 4;
    const notActualCount = 6;
    getSpinTop(turnId || '')
      .then(res => {
        // 转换API返回的数据为UserTopDataItem格式
        const actualPrizes: UserTopDataItem[] = res
          .filter(item => item.level != null && item.awardType === 'REAL')
          .map(item => ({
            player: item.userPhone || '1234****56',
            prize: item.awardName,
            isActual: true,
          }));

        const notActualPrizes: UserTopDataItem[] = res
          .filter(item => item.level == null || item.awardType !== 'REAL')
          .map(item => ({
            player: item.userPhone || '1234****56',
            prize: item.awardName,
            isActual: false,
          }));

        let finalActualPrizes = actualPrizes.slice(0, actualCount);
        let finalNotActualPrizes = notActualPrizes.slice(0, notActualCount);
        const randomPrizes = generateMockData(
          prizeList,
          Math.max(notActualCount - finalNotActualPrizes.length, 0),
          Math.max(actualCount - finalActualPrizes.length, 0),
        );
        finalActualPrizes = [...finalActualPrizes, ...randomPrizes];
        finalNotActualPrizes = [...finalNotActualPrizes, ...randomPrizes];
        setUserTopList(
          [...finalActualPrizes, ...finalNotActualPrizes].sort(
            () => Math.random() - 0.5,
          ),
        );
      })
      .catch(e => {
        console.error(e);
        setUserTopList(
          generateMockData(prizeList, notActualCount, actualCount).sort(
            () => Math.random() - 0.5,
          ),
        );
      });
  };

  useEffect(() => {
    if (turnId != null) {
      updateSpinTop();
    } else {
      setUserTopList(
        generateMockData(prizeList, 4, 6).sort(() => Math.random() - 0.5),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnId]);

  const handleWinRecord = () => {
    onClose();
    if (login) {
      goTo('WinningRecord');
    } else {
      goTo('Login');
    }
  };
  const [spinCount, setSpinCount] = useState(0);
  useEffect(() => {
    if (login) {
      const turnableConfig = localStorage.getItem('turnableConfig');
      if (turnableConfig) {
        try {
          const turnableConfigObj = JSON.parse(turnableConfig);
          setTurnInfoList(turnableConfigObj.info || []);
          setTurnId(turnableConfigObj.id);
        } catch (error) {
          console.log('json parse error turnableConfig');
        }
      }
      getUserWeightInfo().then(res => {
        setTurnId(res.turnableId);
        getActualSpinInfo(res.turnableId).then(actres => {
          console.log(actres);
          localStorage.setItem('turnableConfig', JSON.stringify(actres));
          setTurnInfoList(actres.info || []);
        });
      });
      updateSpinCount();
    }
  }, [login]);

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center p-4 w-[23.4375rem] relative',
        !smallHeight ? 'h-screen' : 'h-[48rem]',
      )}>
      {/* 背景容器x3 */}
      <div
        className="absolute top-0 left-0 w-full h-[50.75rem] bg-full bg-no-repeat z-[1]"
        style={{
          backgroundImage: `url(${require('@/assets/imgs/spin-actual/spin-bg.webp')})`,
        }}
      />
      <div
        className="absolute top-0 left-0 w-full h-[50.75rem] bg-full bg-no-repeat z-[5]"
        style={{
          backgroundImage: `url(${require('@/assets/imgs/spin-actual/spin-wrap.webp')})`,
        }}>
        <div
          className="absolute z-10 size-20 top-[20.5rem] left-1/2 -translate-x-1/2 flex flex-col items-center justify-center text-[#6F0F00]"
          onClick={handleSpin}>
          <span className="text-[1.375rem] font-bold">SPIN</span>
          <span className="text-sm font-normal -mt-1">x{spinCount}</span>
        </div>
      </div>
      <div className="absolute top-[12.65625rem] left-1/2 -translate-x-1/2 size-[19.5rem] z-[3]">
        {/* 转盘容器 */}
        <WheelContainer
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: isSpinning
              ? `transform ${
                  1000 * baseRotateCount
                }ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
              : 'none',
          }}
        />
      </div>
      {/* 各种操作内容 */}
      <div
        className={cn(
          'absolute top-[3.75rem] px-3 left-0 right-0 flex flex-row items-center justify-between z-10',
          smallHeight ? 'top-[4.5rem]' : 'top-[3.75rem]',
        )}>
        <Button
          className="size-6 bg-transparent border-none p-0 m-0"
          onClick={handleRules}>
          <QuestionIcon className="size-6" />
        </Button>
        <Button
          className="size-6 bg-transparent border-none p-0 m-0"
          onClick={() => {
            goToNext();
          }}>
          <CloseIcon className="size-6" />
        </Button>
      </div>
      {userTopList.length > 0 && (
        <Swiper
          className="absolute top-[34.5rem] w-full z-10 h-4"
          slidesPerView={1}
          spaceBetween={0}
          autoplay={{delay: 3000}}
          loop
          modules={[Autoplay]}
          direction="vertical">
          {userTopList.map((item, index) => (
            <SwiperSlide
              key={index}
              className="flex flex-row justify-center items-center gap-2 h-4">
              <div
                className="size-4 bg-full bg-no-repeat"
                style={{
                  backgroundImage: `url(${require('@/assets/icons/spin-actual/trumpet.webp')})`,
                }}
              />

              <div className="text-center text-xs text-white opacity-80">
                <Trans
                  i18nKey="spinActual.tip.getPrize"
                  values={item}
                  components={[
                    <span key="prize" className="text-[#FFEA1D] font-bold" />,
                  ]}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      <div
        className={cn(
          'absolute top-[39.5rem] flex flex-col items-center left-1/2 -translate-x-1/2 z-10 gap-3',
          smallHeight ? 'top-[37.5rem]' : 'top-[39.5rem]',
        )}>
        <Button
          className="w-[18.3125rem] h-[3.5625rem] bg-full bg-no-repeat bg-transparent"
          style={{
            backgroundImage: `url(${require('@/assets/icons/spin-actual/btn-recharge.webp')})`,
          }}
          onClick={() => {
            onClose();
            if (login) {
              goTo('Recharge');
            } else {
              goTo('Login');
            }
          }}
        />
        <p
          className="text-[#FFDC63] text-xs underline"
          onClick={handleWinRecord}>
          {t('spinActual.label.winRecord')}
        </p>
      </div>
      <SpinActualRules ref={rulesRef} />
      <PrizeModal ref={prizeRef} />
    </div>
  );
};

export default SpinActualContent;
